import React from 'react';
import { FiUser, FiClock, FiSlash } from 'react-icons/fi';
import { SchedulePanel } from "@/components/ui/DeviceCard/schedulePanel";
import { AuxWithSchedules, ScheduleInfo } from '@/types/dashboard';
import { getWeekdayArray } from '@/utils/utils';
import { getNextActionTextBySlot } from '@/utils/schedule';
import Switch from '@mui/material/Switch';
import { formatMinutesToTime } from '@/utils/time';
import { LoRaIcon } from '@/components/icons/LoRaIcon';
import { AuxTypeCode } from '@/types/device';
import { formatAuxTimerCountdown, formatAuxTimerDuration } from '@/utils/auxTimer';

interface SpaSectionProps {
  spaList: Array<AuxWithSchedules>;
  spaDevices: Array<AuxWithSchedules>;
  scheduleInfo: ScheduleInfo | undefined;
  onSpaToggle: (typeCode: number, slotNum: number, IsOn: boolean) => void;
  onSpaDeviceToggle: (typeCode: number, slotNum: number, IsOn: boolean) => void;
  isCleanerOn: boolean;
  isHeaterCoolDown: boolean;
}

const SpaSection: React.FC<SpaSectionProps> = ({
  spaList,
  spaDevices,
  scheduleInfo,
  onSpaToggle,
  onSpaDeviceToggle,
  isCleanerOn,
  isHeaterCoolDown
}) => {
  const pickSpaModeEntry = (list: Array<AuxWithSchedules>): AuxWithSchedules | null => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return (
      list.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_RETURN) ||
      list.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_SUCTION) ||
      list.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_1) ||
      list.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_2) ||
      list[0]
    );
  };

  const spaMode = pickSpaModeEntry(spaList);
  const hasSpaModeSchedule = Boolean(
    scheduleInfo?.schedules?.some(
      (s) =>
        spaMode &&
        s.aux_type_code === spaMode.aux.aux_type_code &&
        (s.aux_slot ?? s.slot_num) === spaMode.aux.aux_slot_num &&
        s.schedule_state
    )
  );
  
  return (
    <div className="space-y-6">
      {/* Spa Mode Section */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-900 rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">Spa Mode</h3>
        {!spaMode ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No spa configured</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div key={`${spaMode.aux.aux_type_code}-${spaMode.aux.aux_slot_num}`} className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-3 h-3 rounded-full animate-pulse ${
                      spaMode.aux.aux_status ? "bg-emerald-500" : "bg-slate-400"
                    }`}
                  ></div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-gray-100 text-md">
                      Spa Mode
                    </h4>
                    <div className="flex items-center text-sm text-slate-600 dark:text-gray-300 mt-1">
                      {spaMode.aux.aux_trump ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2">
                          <FiUser className="mr-1" />
                          Manual
                        </span>
                      ) : hasSpaModeSchedule ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2">
                          <FiClock className="mr-1" />
                          Scheduled
                        </span>
                      ) : null}
                      {scheduleInfo && (() => {
                        const nextAction =
                          spaMode
                            ? getNextActionTextBySlot(
                          scheduleInfo,
                          spaMode.aux.aux_type_code,
                          spaMode.aux.aux_slot_num
                        )
                            : '';
                        return nextAction ? (
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2 ${
                            spaMode.aux.aux_status ? "text-emerald-500 dark:text-emerald-400" : "text-slate-500 dark:text-gray-400"
                          }`}>
                            {nextAction}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
                {isCleanerOn ? (
                  <div
                    className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500 cursor-not-allowed"
                    title="Cleaner 正在运行，Spa 开关不可用"
                  >
                    <FiSlash className="w-5 h-5" />
                  </div>
                ) : isHeaterCoolDown ? (
                  <div
                    className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500 cursor-not-allowed"
                    title="Heater Cooldown，Spa 开关不可用"
                  >
                    <FiSlash className="w-5 h-5" />
                  </div>
                ) : (
                  <Switch
                    checked={spaMode.aux.aux_status}
                    onChange={() =>
                      onSpaToggle(
                        spaMode.aux.aux_type_code,
                        spaMode.aux.aux_slot_num,
                        !spaMode.aux.aux_status
                      )
                    }
                  />
                )}
              </div>

              {(spaMode.aux.aux_timer ?? 0) > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  <span className="inline-flex w-fit items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    <FiClock className="mr-1.5 h-3.5 w-3.5" />
                    Timer {formatAuxTimerDuration(spaMode.aux.aux_timer ?? 0)}
                  </span>
                  {!!spaMode.aux.aux_status && (spaMode.aux.aux_timer_remain_sec ?? 0) > 0 && (
                    <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      {formatAuxTimerCountdown(spaMode.aux.aux_timer_remain_sec ?? 0)}
                    </span>
                  )}
                </div>
              )}

              {/* Schedule Info */}
              {Array.isArray(spaMode.schedules) && spaMode.schedules.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-800 dark:text-gray-100 flex items-center">
                    <FiClock className="w-4 h-4 mr-2 text-slate-600 dark:text-gray-300" />
                    Schedule
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {spaMode.schedules.map((schedule) => (
                      <div key={schedule.schedule_id} className="h-fit">
                        <SchedulePanel
                          schedule={{
                            id: schedule.schedule_id.toString(),
                            name: schedule.schedule_name,
                            time: `${formatMinutesToTime(schedule.start_time)} - ${formatMinutesToTime(schedule.end_time)}`,
                            days: getWeekdayArray(schedule.week_days),
                            enabled: schedule.schedule_state,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Spa Devices Section */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-900 rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">Spa Devices</h3>
        {spaDevices.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No spa devices configured</p>
          </div>
        ) : (
          <div className="space-y-6">
            {spaDevices.map((device) => (
              <div key={`${device.aux.aux_type_code}-${device.aux.aux_slot_num}`} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-3 h-3 rounded-full animate-pulse ${
                        device.aux.aux_status ? "bg-emerald-500" : "bg-slate-400"
                      }`}
                    ></div>
                    <div>
                      <div className="flex items-center">
                        <h4 className="font-semibold text-slate-900 dark:text-gray-100 text-md">
                          {device.aux.aux_tag}
                        </h4>
                        {device.aux.aux_slot_num > 11 && (
                          <LoRaIcon className="ml-2" size={16} />
                        )}
                      </div>
                      <div className="flex items-center text-sm text-slate-600 dark:text-gray-300 mt-1">
                        {device.aux.aux_trump ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2">
                            <FiUser className="mr-1" />
                            Manual
                          </span>
                        ) : Boolean(
                          scheduleInfo?.schedules?.some(
                            (s) =>
                              s.aux_type_code === device.aux.aux_type_code &&
                              (s.aux_slot ?? s.slot_num) === device.aux.aux_slot_num &&
                              s.schedule_state
                          )
                        ) ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2">
                            <FiClock className="mr-1" />
                            Scheduled
                          </span>
                        ) : null}
                        {scheduleInfo && (() => {
                          const nextAction = getNextActionTextBySlot(
                            scheduleInfo,
                            device.aux.aux_type_code,
                            device.aux.aux_slot_num
                          );
                          return nextAction ? (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs border border-slate-200 dark:border-gray-700 rounded mr-2 ${
                              device.aux.aux_status ? "text-emerald-500 dark:text-emerald-400" : "text-slate-500 dark:text-gray-400"
                            }`}>
                              {nextAction}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                  {isCleanerOn ? (
                    <div
                      className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500 cursor-not-allowed"
                      title="Cleaner 正在运行，Spa 开关不可用"
                    >
                      <FiSlash className="w-5 h-5" />
                    </div>
                  ) : isHeaterCoolDown ? (
                    <div
                      className="p-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500 cursor-not-allowed"
                      title="Heater Cooldown，Spa 开关不可用"
                    >
                      <FiSlash className="w-5 h-5" />
                    </div>
                  ) : (
                    <Switch
                      checked={device.aux.aux_status}
                      onChange={() =>
                        onSpaDeviceToggle(
                          device.aux.aux_type_code,
                          device.aux.aux_slot_num,
                          !device.aux.aux_status
                        )
                      }
                    />
                  )}
                </div>

                {(device.aux.aux_timer ?? 0) > 0 && (
                  <div className="mt-2 flex flex-col gap-1">
                    <span className="inline-flex w-fit items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">
                      <FiClock className="mr-1.5 h-3.5 w-3.5" />
                      Timer {formatAuxTimerDuration(device.aux.aux_timer ?? 0)}
                    </span>
                    {!!device.aux.aux_status && (device.aux.aux_timer_remain_sec ?? 0) > 0 && (
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        {formatAuxTimerCountdown(device.aux.aux_timer_remain_sec ?? 0)}
                      </span>
                    )}
                  </div>
                )}

                {/* Schedule Info */}
                {Array.isArray(device.schedules) && device.schedules.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-slate-800 dark:text-gray-100 flex items-center">
                      <FiClock className="w-4 h-4 mr-2 text-slate-600 dark:text-gray-300" />
                      Schedule
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {device.schedules.map((schedule) => (
                        <div key={schedule.schedule_id} className="h-fit">
                          <SchedulePanel
                            schedule={{
                              id: schedule.schedule_id.toString(),
                              name: schedule.schedule_name,
                              time: `${formatMinutesToTime(schedule.start_time)} - ${formatMinutesToTime(schedule.end_time)}`,
                              days: getWeekdayArray(schedule.week_days),
                              enabled: schedule.schedule_state,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaSection;
